import type { IPlatform, NativeBridge, SecureStore } from './types'
import { webPlatform } from './web-platform'

/**
 * Electron platform — routes through the native bridge injected by the
 * preload script (`window.edge`). Falls back to web behavior for any
 * method the bridge doesn't implement, so a partial preload (e.g. early
 * Electron versions) still functions.
 */
export function createElectronPlatform(bridge: NativeBridge): IPlatform {
  const bridgedSecureStore: SecureStore = bridge.secureStore
    ? {
        get: (key) => bridge.secureStore!.get(key),
        set: (key, value) => bridge.secureStore!.set(key, value),
        remove: (key) => bridge.secureStore!.remove(key),
      }
    : webPlatform.secureStore

  // One bridge subscription for the window's lifetime keeps isFullScreen()
  // current even before anything subscribes; page listeners fan out from it.
  let fullScreen = bridge.isFullScreen ?? false
  const listeners = new Set<(fullScreen: boolean) => void>()
  bridge.onFullScreenChange?.((value) => {
    fullScreen = value
    for (const listener of listeners) listener(value)
  })

  return {
    kind: 'electron',
    isNative: true,
    secureStore: bridgedSecureStore,
    openExternal: bridge.openExternal ?? webPlatform.openExternal,
    titleBar: bridge.titleBar ?? 'native',
    isFullScreen: () => fullScreen,
    onFullScreenChange(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
